import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Calendar, Edit, Plus, DollarSign, TrendingUp, Upload, List } from 'lucide-react';
import { format } from 'date-fns';
import { buildApiUrl } from '@/lib/config';
import BulkForexRatesForm from './BulkForexRatesForm';

interface ForexRate {
  id: number;
  currencyCode: string;
  rate: string;
  date: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export default function ForexRatesManagement() {
  const [editingRate, setEditingRate] = useState<ForexRate | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [editValue, setEditValue] = useState('');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch forex rates with better date handling
  const { data: allForexRates = [], isLoading, refetch } = useQuery({
    queryKey: ['/api/forex-rates/all'],
    queryFn: async () => {
      // Get today's rates first (public endpoint)
      let response = await fetch(buildApiUrl('api/forex-rates/today'));
      
      if (!response.ok) {
        throw new Error('Failed to fetch forex rates');
      }
      
      const todayRates = await response.json();
      
      // Try to get historical rates if available (this may fail due to auth, that's OK)
      try {
        const token = localStorage.getItem('authToken');
        const historicalResponse = await fetch(buildApiUrl('api/forex-rates'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (historicalResponse.ok) {
          const historicalRates = await historicalResponse.json();
          // Combine and deduplicate
          const combined = [...historicalRates, ...todayRates];
          const unique = combined.filter((rate, index, arr) => 
            arr.findIndex(r => r.id === rate.id) === index
          );
          return unique;
        }
      } catch (error) {
        console.warn('Historical forex rates unavailable, showing today only');
      }
      
      return todayRates;
    },
  });

  // Filter rates by selected date
  const forexRates = selectedDate 
    ? allForexRates.filter((rate: any) => {
        const rateDate = new Date(rate.date).toISOString().split('T')[0];
        return rateDate === selectedDate;
      })
    : allForexRates;

  // Update forex rate mutation
  const updateRateMutation = useMutation({
    mutationFn: async ({ id, rate }: { id: number; rate: string }) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`api/forex-rates/${id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          rate: parseFloat(rate).toString(),
          updatedAt: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update error:', errorText);
        throw new Error(`Failed to update forex rate: ${response.status}`);
      }
      
      return response.json();
    },
    onSuccess: () => {
      setEditingRate(null);
      setEditValue('');
      refetch();
      toast({
        title: "Success",
        description: "Forex rate updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (rate: ForexRate) => {
    setEditingRate(rate);
    setEditValue(rate.rate);
  };

  const handleSaveEdit = () => {
    if (!editingRate) return;
    
    const rateValue = parseFloat(editValue);
    if (rateValue <= 0 || isNaN(rateValue)) {
      toast({
        title: "Error",
        description: "Rate must be a positive number",
        variant: "destructive",
      });
      return;
    }

    updateRateMutation.mutate({
      id: editingRate.id,
      rate: editValue,
    });
  };

  const handleCancelEdit = () => {
    setEditingRate(null);
    setEditValue('');
  };

  const handleBulkFormSuccess = () => {
    refetch();
  };

  const todaysRates = forexRates.filter((rate: any) => 
    rate.date === new Date().toISOString().split('T')[0]
  );

  // Transform the data to ensure consistent field names
  const filteredRates = forexRates.map((rate: any) => ({
    id: rate.id,
    currencyCode: rate.currencyCode || rate.currency_code,
    rate: rate.rate,
    date: rate.date,
    createdAt: rate.createdAt || rate.created_at,
    createdBy: rate.createdBy || rate.created_by,
    updatedAt: rate.updatedAt || rate.updated_at,
    updatedBy: rate.updatedBy || rate.updated_by
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Currencies</p>
                <p className="text-2xl font-bold">{new Set(forexRates.map((r: any) => r.currencyCode || r.currency_code)).size}</p>
              </div>
              <DollarSign className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Rates</p>
                <p className="text-2xl font-bold">{todaysRates.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Last Updated</p>
                <p className="text-sm font-bold">
                  {forexRates.length > 0 ? (() => {
                    try {
                      const validDates = forexRates
                        .map((r: any) => new Date(r.updatedAt || r.updated_at))
                        .filter(date => !isNaN(date.getTime()));
                      
                      if (validDates.length > 0) {
                        const latestDate = new Date(Math.max(...validDates.map(d => d.getTime())));
                        return format(latestDate, 'HH:mm');
                      }
                      return 'Never';
                    } catch (error) {
                      return 'Never';
                    }
                  })() : 'Never'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Bulk Entry Form */}
        <BulkForexRatesForm onSuccess={handleBulkFormSuccess} />
        
        {/* Rates Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Current Exchange Rates</span>
              <div className="flex items-center space-x-4">
                <Label htmlFor="filter-date">Filter by Date:</Label>
                <Input
                  id="filter-date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-40"
                />
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="text-gray-500">Loading forex rates...</div>
              </div>
            ) : filteredRates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No forex rates found for the selected date.</p>
                <p className="text-sm text-gray-400 mt-2">Use the bulk entry form above to add rates for this date.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[80px]">Currency</TableHead>
                      <TableHead className="min-w-[100px]">Rate</TableHead>
                      <TableHead className="min-w-[100px]">Date</TableHead>
                      <TableHead className="min-w-[120px] hidden md:table-cell">Created At</TableHead>
                      <TableHead className="min-w-[100px] hidden md:table-cell">Created By</TableHead>
                      <TableHead className="min-w-[120px]">Updated At</TableHead>
                      <TableHead className="min-w-[100px]">Updated By</TableHead>
                      <TableHead className="min-w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRates.map((rate: any) => (
                      <TableRow key={rate.id}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {rate.currencyCode || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {editingRate?.id === rate.id ? (
                            <div className="flex items-center space-x-1">
                              <Input
                                type="number"
                                step="0.0001"
                                min="0"
                                value={editValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
                                    setEditValue(value);
                                  }
                                }}
                                className="w-24 text-sm"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit();
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={updateRateMutation.isPending}
                                className="px-2 text-xs"
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                                className="px-2 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <span className="font-mono text-sm">{parseFloat(rate.rate).toFixed(4)}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={rate.date === new Date().toISOString().split('T')[0] ? 'default' : 'secondary'} className="text-xs">
                            {(() => {
                              try {
                                const date = new Date(rate.date);
                                return isNaN(date.getTime()) ? rate.date : format(date, 'MMM dd');
                              } catch {
                                return rate.date;
                              }
                            })()}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 hidden md:table-cell">
                          <div className="space-y-1">
                            {(() => {
                              try {
                                const dateValue = rate.createdAt;
                                if (!dateValue) return <div>N/A</div>;
                                const date = new Date(dateValue);
                                if (isNaN(date.getTime())) {
                                  return <div>{dateValue}</div>;
                                }
                                return (
                                  <>
                                    <div>{format(date, 'MMM dd, yyyy')}</div>
                                    <div className="text-gray-400">{format(date, 'HH:mm:ss')}</div>
                                  </>
                                );
                              } catch {
                                return <div>N/A</div>;
                              }
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 hidden md:table-cell">
                          <span className="px-2 py-1 bg-blue-50 rounded text-blue-700">
                            {rate.createdBy || 'admin-001'}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          <div className="space-y-1">
                            {(() => {
                              try {
                                const dateValue = rate.updatedAt;
                                if (!dateValue) return <div>N/A</div>;
                                const date = new Date(dateValue);
                                if (isNaN(date.getTime())) {
                                  return <div>{dateValue}</div>;
                                }
                                return (
                                  <>
                                    <div>{format(date, 'MMM dd, yyyy')}</div>
                                    <div className="text-gray-400">{format(date, 'HH:mm:ss')}</div>
                                  </>
                                );
                              } catch {
                                return <div>N/A</div>;
                              }
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          <span className="px-2 py-1 bg-green-50 rounded text-green-700">
                            {rate.updatedBy || 'admin-001'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {editingRate?.id === rate.id ? null : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(rate)}
                              className="px-2 py-1"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}